import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CancelGameInvitesPayload,
  ConnectionActionResponse,
  CreateGameInvitesPayload,
  GAME_INVITES_API,
  GAME_INVITES_CANCEL_API,
} from 'constants/payloads/connections';
import {
  EventPlayerInviteStatusesEnum,
  useGetEventPlayerInvitesQuery,
} from 'types/generated/client';
import getFirstNameLastInitial from 'utils/shared/string/getFirstNameLastInitial';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useApiGateway } from 'hooks/useApi';
import { useViewer } from 'hooks/useViewer';
import type { FindGamePlayer } from 'screens/Find/types';

type InvitePayload = CreateGameInvitesPayload | CancelGameInvitesPayload;

/**
 * The invites on one game, plus the write that reconciles them.
 *
 * `event_player_invites` is select-only for the `user` role, so saving posts to
 * the `v1/game-invites` routes. The save is a diff rather than a replace: an
 * ACCEPTED invite is a real seat (an ACCEPTED play request) and must not be
 * withdrawn by the picker, so only PENDING rows the host dropped are cancelled.
 */
export const useGameInvites = (eventId: string | null) => {
  const { post } = useApiGateway<InvitePayload, ConnectionActionResponse>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { userId } = useViewer();
  // Anonymous can't read invites, so a pre-auth run would come back empty and the
  // edit form would seed (and then save) an empty list, withdrawing every invite.
  const { data, refetch } = useGetEventPlayerInvitesQuery({
    variables: { eventId },
    skip: !eventId || !userId,
  });

  const { invitedPlayerIds, pendingPlayerIds, pendingInvitedPlayers } = useMemo(() => {
    const rows = data?.eventPlayerInvites ?? [];
    const pending = rows.filter((row) => row.status === EventPlayerInviteStatusesEnum.Pending);

    return {
      invitedPlayerIds: rows.map((row) => row.invitedUserId as string),
      pendingPlayerIds: pending.map((row) => row.invitedUserId as string),
      // Drawn into the roster as awaiting-acceptance seats, never as confirmed ones.
      pendingInvitedPlayers: pending.map<FindGamePlayer>((row) => {
        const profile = row.invitedUserProfile;
        const displayName = profile?.preferredName || profile?.fullName || 'Player';

        return {
          id: row.invitedUserId as string,
          name: getFirstNameLastInitial(displayName),
          imageUrl: getProfileImageUrlOrPlaceholder({ path: profile?.profileImagePath }),
          isInvited: true,
        };
      }),
    };
  }, [data]);

  /** Sends invites for a freshly published game, where there is nothing to diff. */
  const invitePlayers = useCallback(
    async (targetEventId: string, playerIds: string[]): Promise<boolean> => {
      if (playerIds.length === 0) return true;

      setIsSubmitting(true);
      try {
        const response = await post({
          endpoint: GAME_INVITES_API,
          payload: { eventId: targetEventId, invitedUserIds: playerIds },
        });

        if (response.isError) {
          toast.error(response.error || 'Could not invite these players');
          return false;
        }

        return true;
      } finally {
        setIsSubmitting(false);
      }
    },
    [post],
  );

  /** Reconciles the picker's selection against what the game already holds. */
  const saveInvites = useCallback(
    async (nextPlayerIds: string[]): Promise<boolean> => {
      if (!eventId) return false;

      const next = new Set(nextPlayerIds);
      const added = nextPlayerIds.filter((id) => !invitedPlayerIds.includes(id));
      // Only PENDING rows can be withdrawn — an accepted player holds a seat.
      const removed = pendingPlayerIds.filter((id) => !next.has(id));

      if (added.length === 0 && removed.length === 0) return true;

      setIsSubmitting(true);
      try {
        if (removed.length > 0) {
          const response = await post({
            endpoint: GAME_INVITES_CANCEL_API,
            payload: { eventId, invitedUserIds: removed },
          });

          if (response.isError) {
            toast.error(response.error || 'Could not withdraw these invites');
            return false;
          }
        }

        if (added.length > 0) {
          const response = await post({
            endpoint: GAME_INVITES_API,
            payload: { eventId, invitedUserIds: added },
          });

          if (response.isError) {
            toast.error(response.error || 'Could not invite these players');
            return false;
          }
        }

        await refetch();
        return true;
      } finally {
        setIsSubmitting(false);
      }
    },
    [eventId, invitedPlayerIds, pendingPlayerIds, post, refetch],
  );

  return {
    invitedPlayerIds,
    pendingPlayerIds,
    pendingInvitedPlayers,
    isLoading: Boolean(eventId) && !data,
    isSubmitting,
    invitePlayers,
    saveInvites,
    refetch,
  };
};
